<script setup>
import {useRoute, useRouter} from "vue-router";
import PrimaryLayout from "@/Layouts/PrimaryLayout.vue";
import {computed, onMounted, onUnmounted, reactive, ref, watch} from "vue";
import {useUserStore} from "@/Stores/userStore";
import {useToast} from "vue-toastification";
import SearchContentList from "@/Pages/Search/SearchContentList.vue";
import { compendiumApi } from "@/Api/compendium-api";
import favoritesApi from "@/Api/favorites-api";
import debounce from "lodash/debounce";
import DieIcon from "@/Components/Icons/DieIcon.vue";
import DarkComboBox from "@/Components/Search/DarkComboBox.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";

const router = useRouter();
const route = useRoute();
const toast = useToast();
const userStore = useUserStore();

const data = reactive({
    searchQuery: {
        input: '',
        edition: "All",
        challengeRating: 'All',
        rangeStart: 0,
        resultLimit: 10,
        randomize: true,
    },
    favorites: {
        creatures: [],
    },
    pagination: null,
    request: null,
    response: null,
    isLoading: true,
})

const isMobile = ref(window.innerWidth < 640);

function onResize() {
    isMobile.value = window.innerWidth < 640;
}

const searchPlaceholder = computed(() => {
    const total = data.response?.total_documents;
    if (!total) return 'Search...';
    const count = total.toLocaleString();
    return isMobile.value
        ? `Search ${count}+ creatures...`
        : `Search our community compendium of ${count}+ creatures...`;
});

const editions = [
    {'id': 0, 'value': "All"},
    {'id': 1, 'value': "5e"},
    {'id': 2, 'value': "4e"},
    {'id': 3, 'value': "2e"},
    {'id': 4, 'value': "Pf2"},
    {'id': 5, 'value': "Pf1"},
    {'id': 6, 'value': "ToA"},
];

const challengeRatings = [
    {'id': 0, 'value': "All"},
    {'id': 1, 'value': "1/8"},
    {'id': 2, 'value': "1/4"},
    {'id': 3, 'value': "1/2"},
    {'id': 4, 'value': "1"},
    {'id': 5, 'value': "2"},
    {'id': 6, 'value': "3"},
    {'id': 7, 'value': "4"},
    {'id': 8, 'value': "5"},
    {'id': 9, 'value': "6"},
    {'id': 10, 'value': "7"},
    {'id': 11, 'value': "8"},
    {'id': 12, 'value': "9"},
    {'id': 13, 'value': "10+"},
]

function postSearchQuery() {
    const request = {
        search_query: data.searchQuery.input,
        randomize: data.searchQuery.randomize,
        edition: data.searchQuery.edition,
        challenge_rating: data.searchQuery.challengeRating,
        range_start: data.searchQuery.rangeStart,
        result_limit: data.searchQuery.resultLimit,
    }
    compendiumApi.postSearchRequest(request).then(response => {
        data.isLoading = false;
        data.response = response.data;
        data.pagination = response.data.pagination;

        const creatureIds = response.data.content.map(item => item.id);
        const request = { creature_ids: creatureIds }

        router.push({
            name: 'search',
            query: {
                query: data.searchQuery.input,
                edition: data.searchQuery.edition,
                challenge_rating: data.searchQuery.challengeRating,
                range_start: data.searchQuery.rangeStart,
                result_limit: data.searchQuery.resultLimit,
            }
        });

        if (creatureIds.length === 0 || !isLoggedIn()) {
            return;
        }
        favoritesApi.checkFavorites(request).then(response => {
            for (const key in response.data.favorites.creatures) {

                // Append and update favorites
                if (!data.favorites.creatures[key]) {
                    data.favorites.creatures[key] = response.data.favorites.creatures[key];
                }
                // Update favorites
                if (data.favorites.creatures[key] !== response.data.favorites.creatures[key]) {
                    data.favorites.creatures[key] = response.data.favorites.creatures[key];
                }
            }
        }).catch(function (error) {
            console.log(error)
            const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
            toast.error(message);
        });
    }).catch(function (error) {
        data.isLoading = false;
        const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
        toast.error(message);
    });
}

const onSearchInput = debounce(() => {
    data.searchQuery.randomize = false
    data.searchQuery.rangeStart = 0;
    data.searchQuery.resultLimit = 10;
    postSearchQuery()
}, 250);

onMounted(() => {
    window.addEventListener('resize', onResize);
    window.scrollTo(0, 0)
    // Get search from route
    const search = typeof route.query.query === 'string' ? route.query.query : '';
    const edition = typeof route.query.edition === 'string' ? route.query.edition : 'All';
    const challengeRating = typeof route.query.challenge_rating === 'string' ? route.query.challenge_rating : 'All';
    const rangeStart = typeof route.query.range_start === 'string' ? route.query.range_start : 0;
    const resultLimit = typeof route.query.result_limit === 'string' ? route.query.result_limit : 10;

    // If search text is not empty
    if (search !== '') {
        data.searchQuery.input = search;
        data.searchQuery.randomize = false
        data.searchQuery.edition = edition;
        data.searchQuery.challengeRating = challengeRating;
        data.searchQuery.rangeStart = rangeStart;
        data.searchQuery.resultLimit = resultLimit;
        postSearchQuery();
    } else {
        postSearchQuery();
    }
})

onUnmounted(() => {
    window.removeEventListener('resize', onResize);
})

const noResults = computed(() => {
    return data.response?.content?.length === 0;
})

const showResetText = computed(() => {
    if (data.searchQuery.edition != null && data.searchQuery.challengeRating != null) {
        return data.searchQuery.edition !== "All" || data.searchQuery.challengeRating !== "All";
    }
    return false;
})

function onDieRoll() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    data.searchQuery.randomize = true;
    postSearchQuery();
}

function resetFilters() {
    data.searchQuery.edition = null;
    data.searchQuery.challengeRating = null;
    onSearchInput();
}

function routeFromUrl(url) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const decodedUrl = decodeURIComponent(url);
    const urlObj = new URL(decodedUrl);
    const urlParams = new URLSearchParams(urlObj.search);
    const range_start = urlParams.get('range_start')
    const result_limit = urlParams.get('result_limit')
    data.searchQuery.rangeStart = range_start;
    data.searchQuery.resultLimit = result_limit;
    postSearchQuery();
}

watch(
    () => data.searchQuery.edition,
    (newVal, oldVal) => {
        if (newVal !== oldVal) {
            onSearchInput();
        }
    }
);

watch(
    () => data.searchQuery.challengeRating,
    (newVal, oldVal) => {
        if (newVal !== oldVal) {
            onSearchInput();
        }
    }
);

function isLoggedIn() {
    const userId = userStore.getUserIdFromToken()
    if (userId == null) {
        return false
    }
    return true
}

</script>

<template>
    <title>Tavern of Azoth - Search</title>
    <meta name="description" content="Search - Explore all creatures created for the community."/>

    <PrimaryLayout>
        <div class="bg-gray-900 relative">
            <div class="content-wrapper z-1 min-h-screen">
                <div class="background-image"/>

                <div class="flex justify-center px-4">
                    <div class="max-w-5xl w-full" style="z-index: 1">
                        <div class="grid gap-2 items-center mt-8" style="grid-template-columns: repeat(7, 1fr) auto;">
                            <!-- Search bar -->
                            <div class="col-span-8 md:col-span-5 relative">
                                <input v-model="data.searchQuery.input" @input="onSearchInput" type="text" :placeholder="searchPlaceholder" class="p-2 pl-10 w-full bg-gray-800 rounded-md text-white">
                                <div class="absolute top-0 left-0 -mt-0.5 ml-0 inline-flex items-center p-3">
                                    <svg class="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-6a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                                    </svg>
                                </div>
                            </div>
                            <!-- Edition combo box -->
                            <div class="col-span-3 md:col-span-1">
                                <DarkComboBox label="" placeholder="Edition" :options="editions" :default-option="null" :selected-option-value="data.searchQuery.edition" @selected="data.searchQuery.edition = $event"/>
                            </div>
                            <!-- Challenge combo box -->
                            <div class="col-span-3 md:col-span-1">
                                <DarkComboBox label="" placeholder="Challenge" :options="challengeRatings" :default-option="null" :selected-option-value="data.searchQuery.challengeRating" @selected="data.searchQuery.challengeRating = $event"/>
                            </div>
                            <!-- Dice icon -->
                            <div class="col-span-2 md:col-span-1 flex justify-end w-8">
                                <DieIcon @animation-finished="onDieRoll" aria-label="Randomize"/>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="noResults" class="flex flex-col justify-center items-center px-4 mt-20">
                    <p class="text-white">No Results</p>
                    <p v-if="showResetText" @click="resetFilters" class="text-blue-500 cursor-pointer mt-2 z-10">
                        Try resetting filters
                    </p>
                </div>


                <div class="flex justify-center px-4 z-1">
                    <SearchContentList class="" v-if="data.response?.content"
                                       :content="data.response.content"
                                       :favorites="data.favorites"
                    />
                </div>
                <div class="flex justify-center px-4">
                    <div class="max-w-5xl w-full z-10">
                        <nav v-if="data.pagination?.previous_url || data.pagination?.next_url" class="mt-4 mb-12 flex items-center justify-between px-4 py-3 sm:px-6" aria-label="Pagination">
                            <div class="hidden sm:block">
                                <p class="text-sm text-gray-200">
                                    Showing
                                    <span class="font-medium">{{data.pagination.range_start}}</span>
                                    to
                                    <span class="font-medium">{{data.pagination.range_end}}</span>
                                    of
                                    <span class="font-medium">{{data.pagination.total_items}}</span>
                                    results
                                </p>
                            </div>
                            <div class="flex flex-1 justify-between sm:justify-end">
                                <PrimaryButton :disabled="!data.pagination.previous_url" class="mr-2" @click="routeFromUrl(data.pagination.previous_url)">Previous</PrimaryButton>
                                <PrimaryButton :disabled="!data.pagination.next_url" class="ml-2" @click="routeFromUrl(data.pagination.next_url)">Next</PrimaryButton>
                            </div>
                        </nav>
                        <div v-else>
                            <nav v-if="data.response?.total_documents && data.response?.content?.length !== 0" class="mt-4 mb-12 flex items-center justify-between px-4 py-3 sm:px-6" aria-label="Pagination">
                                <p class="text-base text-gray-200">Searchable Creatures: {{data.response.total_documents}}</p>
                                <div class="flex flex-1 justify-between sm:justify-end">
                                    <PrimaryButton class="ml-2" @click="onDieRoll">Random</PrimaryButton>
                                </div>
                            </nav>
                        </div>
                    </div>
                    <div class="mb-16"></div>
                </div>
            </div>
        </div>
    </PrimaryLayout>
</template>

<style scoped>
/*noinspection CssUnknownTarget*/
.background-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.1;
    z-index: 0;
    background-image: url("/api/images/Features/compendium_background.png");
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center center;
    background-attachment: fixed;
}
</style>
